import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useAuth } from '@repletoryreach/sdk-auth';
import { isApiError } from '@repletoryreach/sdk-api';
import { Stack, Button, Group } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../../i18n/I18nProvider';

/** Seconds before the confirmation lets you send another email. */
const RESEND_COOLDOWN = 30;

export function ForgotForm() {
  const t = useT();
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const sentRef = useRef<HTMLDivElement | null>(null);

  // Countdown for the resend button.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  // Swapping the form for an alert is a large DOM change; without focus a
  // screen-reader user hears nothing after pressing the button.
  useEffect(() => { if (sent) sentRef.current?.focus(); }, [sent]);

  async function send() {
    setBusy(true);
    setError(null);
    try {
      await forgotPassword(email);
      setSent(true);
      setCooldown(RESEND_COOLDOWN);
    } catch (err) {
      /*
       * This used to set `sent` in a `finally`, so a 500, a timeout or an
       * offline browser all rendered "Check your inbox" and the user waited
       * for an email that was never sent (auth-forgot-password.md §4 item 1).
       *
       * The account-enumeration defence only requires that a *200* be
       * indistinguishable between a known and an unknown address. It does not
       * require that a transport failure be reported as success: so a 4xx is
       * still treated as "sent", and only 5xx/network errors surface.
       */
      if (isApiError(err) && err.status < 500) {
        setSent(true);
        setCooldown(RESEND_COOLDOWN);
      } else {
        setError(t('auth.forgot.errors.unavailable'));
      }
    } finally {
      setBusy(false);
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    void send();
  }

  if (sent) {
    return (
      <Stack gap="md">
        <Alert
          color="info"
          role="status"
          tabIndex={-1}
          ref={sentRef}
          title={t('auth.forgot.sent.title')}
        >
          {t('auth.forgot.sent.body', { email })}
        </Alert>
        {error && <Alert color="error" role="alert">{error}</Alert>}
        <Group gap="sm">
          <Button
            size="xs"
            variant="default"
            loading={busy}
            disabled={cooldown > 0}
            onClick={() => { void send(); }}
          >
            {cooldown > 0 ? t('auth.forgot.sent.resendIn', { seconds: cooldown }) : t('auth.forgot.sent.resend')}
          </Button>
          {/* Once `sent` flipped there was no way back to fix a typo'd address. */}
          <Button size="xs" variant="subtle" onClick={() => { setSent(false); setError(null); }}>
            {t('auth.forgot.sent.changeEmail')}
          </Button>
        </Group>
      </Stack>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        <TextInput
          label={t('common.email')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          required
          autoComplete="email"
          autoFocus
          placeholder={t('auth.login.emailPlaceholder')}
          leftSection={<MailIcon />}
        />
        {error && <Alert color="error" role="alert">{error}</Alert>}
        <Button type="submit" loading={busy} fullWidth leftSection={<SendIcon />}>
          {t('auth.forgot.submit')}
        </Button>
      </Stack>
    </form>
  );
}

function MailIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="1.75" y="3.25" width="12.5" height="9.5" rx="1.5" />
      <path d="M2 4.5l6 4 6-4" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M14.5 1.5L7 9" />
      <path d="M14.5 1.5l-4.7 13-2.8-5.5L1.5 6.2z" />
    </svg>
  );
}
